import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

const SubscriptionContext = createContext();

export const useSubscription = () => useContext(SubscriptionContext);

const safeJsonParse = (value, fallback) => {
    try {
        return JSON.parse(value);
    } catch (error) {
        return fallback;
    }
};

const safeGetLocalStorage = (key, fallback) => {
    try {
        const raw = localStorage.getItem(key);
        if (raw === null || raw === undefined) return fallback;
        return safeJsonParse(raw, fallback);
    } catch (error) {
        return fallback;
    }
};

// Subscription plans configuration (SaaS Contract V1)
export const PLANS = {
    ESENCIAL: {
        id: 'esencial',
        name: 'Plan Esencial',
        price: 0,
        features: [
            '1 Propiedad Activa',
            'Centro Financiero (Calculadoras)',
            'Perfil Público Básico',
            'Soporte por Comunidad'
        ],
        limits: {
            properties: 1,
            clients: 5,
            documents: 0,
            monthlyReports: 0
        }
    },
    PRO: {
        id: 'pro',
        name: 'Plan Profesional',
        price: 19,
        features: [
            '30 Propiedades',
            'CRM de Gestión de Leads',
            'Publicador Social Profesional (crea textos y posts para tus propiedades)',
            'Branding de Agente',
            'WhatsApp Directo en Propiedades'
        ],
        limits: {
            properties: 30,
            clients: 200,
            documents: 10,
            monthlyReports: 5
        }
    },
    SUPERIOR: {
        id: 'superior',
        name: 'Plan Superior',
        price: 49,
        features: [
            '100 Propiedades',
            'CRM Avanzado e Historial',
            'Generador de Documentos PDF',
            'Publicador Social Profesional Completo',
            'Prioridad en el Catálogo Público',
            'Soporte Prioritario 24/7'
        ],
        limits: {
            properties: 100,
            clients: 10000,
            documents: 100,
            monthlyReports: 30
        }
    },
    AGENCIA: {
        id: 'agencia',
        name: 'Plan Agencia',
        price: 99,
        features: [
            '300 Propiedades',
            'CRM Multi-agente',
            'White Label',
            'Account Manager Dedicado',
            'Capacitación Personalizada',
            'Integración Custom'
        ],
        limits: {
            properties: 300,
            clients: 50000,
            documents: 1000,
            monthlyReports: 100
        }
    },
    // Mantener PREMIUM como alias legacy si es necesario, o eliminarlo si se prefiere limpieza total.
    // Mapearemos PREMIUM a AGENCIA en la lógica de UI si hace falta.

};

// Feature access matrix - Role Based Access Control (RBAC)
export const FEATURE_ACCESS = {
    // Financial Center (Publicly limited to calculators for free)
    'financial.mortgage': ['pro', 'superior', 'premium', 'master'],
    'financial.rates': ['pro', 'superior', 'premium', 'master'],
    'financial.tariffs': ['pro', 'superior', 'premium', 'master'],
    'financial.procedures': ['pro', 'superior', 'premium', 'master'],
    'financial.appraisals': ['pro', 'superior', 'premium', 'master'],
    'financial.map': ['pro', 'superior', 'premium', 'master'],
    'financial.diaspora': ['pro', 'superior', 'premium', 'master'],
    'financial.conversions': ['pro', 'superior', 'premium', 'master'],
    'financial.roi': ['pro', 'superior', 'premium', 'master'],
    'financial.constructionCost': ['pro', 'superior', 'premium', 'master'],
    'financial.commission': ['pro', 'superior', 'premium', 'master'],
    'financial.investment': ['superior', 'premium', 'master'],

    // Operations Center (Dashboard Tools)
    'operations.crm': ['pro', 'superior', 'premium', 'master'],
    'operations.propertyManager': ['esencial', 'pro', 'superior', 'premium', 'master'],
    'operations.documents': ['superior', 'premium', 'master'], // PDF Generation
    'operations.reports': ['pro', 'superior', 'premium', 'master'],
    'crm.leads': ['pro', 'superior', 'premium', 'master'],

    // Marketing Center
    'marketing.hub': ['pro', 'superior', 'premium', 'master'],
    'marketing.aiDescriptions': ['superior', 'premium', 'master'],
    'marketing.aiImages': ['superior', 'premium', 'master'],
    'marketing.adManager': ['pro', 'superior', 'premium', 'master'],

    // Premium Tools
    'tools.exclusiveExpos': ['superior', 'premium', 'master'],
    'tools.prioritySupport': ['superior', 'premium', 'master'],

    // Knowledge Base
    'knowledge.base': ['pro', 'superior', 'premium', 'master']
};

export const SubscriptionProvider = ({ children }) => {
    const { user } = useAuth();
    const [subscription, setSubscription] = useState(null);
    const [loading, setLoading] = useState(true);
    const [planError, setPlanError] = useState(null);
    const defaultPlan = {
        plan: 'esencial',
        status: 'active',
        startDate: new Date().toISOString(),
        endDate: null,
        autoRenew: false
    };

    useEffect(() => {
        const fetchUserPlan = async () => {
            setLoading(true);
            setPlanError(null);

            // If no user is logged in, default to free plan
            if (!user || !user.id) {
                setSubscription(defaultPlan);
                setLoading(false);
                return;
            }

            try {
                if (import.meta.env.VITE_SIMULATE_BAD_API === '1') {
                    setPlanError('No se pudo cargar el plan.');
                    setSubscription(defaultPlan);
                    setLoading(false);
                    return;
                }
                // FETCH PLAN FROM DATABASE (Single Source of Truth)
                const response = await fetch(`/api.php?action=get_user_plan&user_id=${user.id}`).catch(() => null);

                if (!response || !response.ok) {
                    console.warn('Subscription: Plan fetch failed or server unreachable');
                    setPlanError('Error de red al cargar el plan.');
                    setSubscription(defaultPlan);
                    setLoading(false);
                    return;
                }

                const contentType = response.headers.get('content-type') || '';
                let data;

                try {
                    if (!contentType.includes('application/json')) {
                        throw new Error('Response is not JSON');
                    }
                    data = await response.json();
                } catch (e) {
                    console.warn('Subscription: Invalid JSON response', e);
                    setPlanError('Respuesta inválida del servidor.');
                    setSubscription(defaultPlan);
                    setLoading(false);
                    return;
                }

                if (data && data.success) {
                    // Map database plan to frontend format (Case Insensitive)
                    const dbPlan = (data.plan || 'FREE').toUpperCase();
                    const planMap = {
                        'FREE': 'esencial',
                        'GRATUITO': 'esencial',
                        'PRO': 'pro',
                        'PROFESSIONAL': 'pro',
                        'PROFESIONAL': 'pro',
                        'SUPERIOR': 'superior',
                        'PREMIUM': 'premium',
                        'MASTER': 'premium', // Master is top tier
                        'ADMIN': 'premium'   // Admin has full access
                    };

                    const planId = planMap[dbPlan] || 'esencial';
                    const status = (data.status || 'ACTIVE').toUpperCase() === 'ACTIVE' ? 'active' : 'inactive';

                    const startDate = user.created_at ? new Date(user.created_at.replace(' ', 'T')) : new Date();
                    // Fix timezone offset if needed, but assuming UTC or server time is consistent

                    const userSub = {
                        plan: planId,
                        status: status,
                        startDate: startDate.toISOString(),
                        // For paid plans, we ideally fetch this from DB. For now, default to +30 days if active.
                        endDate: status === 'active' ? new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString() : null,
                        autoRenew: status === 'active',
                        dbPlan: data.plan, // Keep original DB plan for reference
                        dbStatus: data.status,
                        // Trial logic: 3 days from registration
                        trialEnd: new Date(startDate.getTime() + 3 * 24 * 60 * 60 * 1000).toISOString()
                    };

                    setSubscription(userSub);
                } else {
                    // Fallback to free plan if API fails
                    console.error('Failed to fetch user plan:', data?.error);
                    setPlanError('Plan no encontrado.');
                    setSubscription(defaultPlan);
                }
            } catch (error) {
                console.error('Error fetching user plan:', error);
                // Fallback to free plan on error
                setPlanError('Error interno al cargar el plan.');
                setSubscription(defaultPlan);
            }

            setLoading(false);
        };

        fetchUserPlan();
    }, [user]);

    const hasAccess = (featureKey) => {
        try {
            if (!subscription) return false;
            const allowedPlans = FEATURE_ACCESS[featureKey];
            if (!allowedPlans) {
                console.warn(`Feature key "${featureKey}" not defined in FEATURE_ACCESS. Blocking by default.`);
                return false;
            }
            return allowedPlans.includes(subscription.plan);
        } catch (error) {
            console.warn('hasAccess error:', error);
            return false;
        }
    };

    const isWithinLimit = (limitType, currentCount) => {
        if (!subscription) return false;
        const plan = PLANS[subscription.plan.toUpperCase()];
        if (!plan) return false;
        const limit = plan.limits[limitType];
        return currentCount < limit;
    };

    const upgradeToPro = async (planType = 'pro') => {
        if (!user || !user.id) {
            console.error('Cannot upgrade: No user logged in');
            return;
        }

        try {
            // Map frontend plan to database plan
            const dbPlanMap = {
                'esencial': 'FREE',
                'pro': 'PRO',
                'superior': 'SUPERIOR',
                'premium': 'PREMIUM',
                'master': 'SUPERIOR'
            };

            const dbPlan = dbPlanMap[planType] || 'PRO';

            // Update plan in DATABASE via API
            const response = await fetch('/api.php?action=users', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    id: user.id,
                    subscription_plan: dbPlan,
                    subscription_status: 'ACTIVE',
                    role: user.role || 'user'
                })
            });

            const data = await response.json();

            if (data.success) {
                // Update local state
                const newSub = {
                    plan: planType,
                    status: 'active',
                    startDate: new Date().toISOString(),
                    endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
                    autoRenew: true,
                    dbPlan: dbPlan,
                    dbStatus: 'ACTIVE'
                };

                setSubscription(newSub);
            } else {
                console.error('Failed to upgrade plan:', data.error);
            }
        } catch (error) {
            console.error('Error upgrading plan:', error);
        }
    };

    const cancelSubscription = () => {
        if (!subscription) return;

        const userId = user?.email || user?.id || 'guest';

        const cancelledSub = {
            ...subscription,
            status: 'cancelled',
            autoRenew: false
        };

        setSubscription(cancelledSub);
        localStorage.setItem(`subscription_${userId}`, JSON.stringify(cancelledSub));
    };

    const getCurrentPlan = () => {
        if (!subscription) return PLANS.ESENCIAL;
        return PLANS[subscription.plan.toUpperCase()] || PLANS.ESENCIAL;
    };

    const value = {
        subscription,
        loading,
        planError,
        hasAccess,
        isWithinLimit,
        upgradeToPro,
        cancelSubscription,
        getCurrentPlan,
        plans: PLANS
    };

    return (
        <SubscriptionContext.Provider value={value}>
            {children}
        </SubscriptionContext.Provider>
    );
};
